/**
 * HW Tabbed Content — <hw-tabs> with child <hw-tab> elements.
 *
 * Lifts each tab trigger into a single tablist and wires keyboard support.
 * Hoists direct-child .hw-image blocks into .hw-tabbed-content__media after
 * the tablist (content_for 'block' cannot use dynamic ids in Liquid).
 * Typography for the tab row comes from Liquid (data-hw-tabs-type-class); for
 * button tabs, classes match snippets/hw-button.liquid (size-style, style,
 * style--blockId, button-preset-n). No per-tab color-scheme overrides — theme
 * colors apply like hw-button.
 *
 * @see snippets/hw-button.liquid default link branch for class pattern
 */
(function () {
  'use strict';

  if (customElements.get('hw-tabs')) return;

  class HwTab extends HTMLElement {}
  if (!customElements.get('hw-tab')) customElements.define('hw-tab', HwTab);

  class HwTabs extends HTMLElement {
    constructor() {
      super();
      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onSectionUnload = this._onSectionUnload.bind(this);
      this._tabs = [];
      this._panels = [];
      this._tablist = null;
      this._initialised = false;
    }

    /**
     * Applies type classes from data-hw-tabs-type-class. Button tabs mirror
     * hw-button: size-style + button|secondary|tertiary + scoped --id +
     * button-preset-n.
     */
    _applyTabTypeClasses() {
      const typeClass = (this.dataset.hwTabsTypeClass || '').trim();
      if (!typeClass) return;

      const tokens = typeClass.split(/\s+/).filter(Boolean);
      const presetToken = tokens.find((t) => t.indexOf('button-preset-') === 0);
      const styleToken = ['button', 'button-secondary', 'button-tertiary'].find(
        (c) => tokens.includes(c)
      );

      this._tabs.forEach((btn) => {
        const scopedId = btn.getAttribute('data-hw-tab-block-id');

        if (styleToken || presetToken) {
          const effectiveStyle =
            styleToken || (presetToken ? 'button' : null);
          if (effectiveStyle) {
            btn.classList.add('size-style');
            btn.classList.add(effectiveStyle);
            if (scopedId) {
              btn.classList.add(`${effectiveStyle}--${scopedId}`);
            }
          }
          if (presetToken) {
            btn.classList.add(presetToken);
          } else if (effectiveStyle) {
            btn.classList.add('button-preset-1');
          }
          return;
        }

        tokens.forEach((cls) => {
          if (cls) btn.classList.add(cls);
        });
      });
    }

    connectedCallback() {
      if (this._initialised) return;
      Promise.resolve().then(() => this._init());

      document.addEventListener('shopify:section:unload', this._onSectionUnload);
    }

    disconnectedCallback() {
      this._teardown();
      document.removeEventListener('shopify:section:unload', this._onSectionUnload);
    }

    _onSectionUnload(event) {
      if (event.target && event.target.contains(this)) {
        this._teardown();
      }
    }

    _init() {
      if (this._initialised) return;

      const hwTabs = Array.from(this.querySelectorAll(':scope > hw-tab'));
      if (hwTabs.length === 0) return;

      const tablist = document.createElement('div');
      tablist.className = 'hw-tabs__tablist';
      tablist.setAttribute('role', 'tablist');
      const tablistLabel = this.getAttribute('data-hw-tabs-tablist-label');
      if (tablistLabel) tablist.setAttribute('aria-label', tablistLabel);

      hwTabs.forEach((hwTab) => {
        const button = hwTab.querySelector(':scope > [data-hw-tab-button]');
        const panel = hwTab.querySelector(':scope > [data-hw-tab-panel]');
        if (!button || !panel) return;

        tablist.appendChild(button);

        this._tabs.push(button);
        this._panels.push(panel);
      });

      if (this._tabs.length === 0) return;

      const mediaNodes = Array.from(this.children).filter((el) => {
        if (!el.classList || !el.classList.contains('hw-image')) return false;
        return el.tagName.toLowerCase() !== 'hw-tab';
      });

      this.prepend(tablist);
      this._tablist = tablist;

      if (mediaNodes.length > 0) {
        const mediaWrap = document.createElement('div');
        mediaWrap.className = 'hw-tabbed-content__media';
        mediaNodes.forEach((node) => {
          mediaWrap.appendChild(node);
        });
        tablist.insertAdjacentElement('afterend', mediaWrap);
      }

      this._applyTabTypeClasses();

      let activeIndex = hwTabs.findIndex((t) =>
        t.hasAttribute('data-open-by-default')
      );
      if (activeIndex < 0) activeIndex = 0;

      this._activate(activeIndex, { focus: false });

      tablist.addEventListener('click', this._onClick);
      tablist.addEventListener('keydown', this._onKeydown);

      this.setAttribute('data-hw-ready', '');
      this._initialised = true;
    }

    _teardown() {
      if (this._tablist) {
        this._tablist.removeEventListener('click', this._onClick);
        this._tablist.removeEventListener('keydown', this._onKeydown);
      }
      this._tabs = [];
      this._panels = [];
      this._tablist = null;
      this._initialised = false;
    }

    _onClick(event) {
      const button = event.target.closest('[data-hw-tab-button]');
      if (!button) return;
      const idx = this._tabs.indexOf(button);
      if (idx < 0) return;
      this._activate(idx, { focus: true });
    }

    /**
     * @returns {boolean}
     */
    _isSliderViewport() {
      const wide =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(min-width: 990px)').matches;
      const override = this.dataset.hwTabsLayoutDesktopOverride === 'true';
      const sliderM = this.dataset.hwTabsSliderM === 'true';
      const sliderD = this.dataset.hwTabsSliderD === 'true';
      if (!override) {
        return sliderM;
      }
      return wide ? sliderD : sliderM;
    }

    /**
     * Keeps the focused tab visible when the row is a horizontal slider.
     *
     * @param {HTMLElement} tab
     */
    _scrollTabIntoViewIfSlider(tab) {
      if (!this._isSliderViewport() || !tab) return;
      const reduce =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      tab.scrollIntoView({
        behavior: reduce ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }

    _onKeydown(event) {
      const current = document.activeElement;
      const currentIdx = this._tabs.indexOf(current);
      if (currentIdx < 0) return;

      let nextIdx = -1;
      switch (event.key) {
        case 'ArrowRight':
          nextIdx = (currentIdx + 1) % this._tabs.length;
          break;
        case 'ArrowLeft':
          nextIdx = (currentIdx - 1 + this._tabs.length) % this._tabs.length;
          break;
        case 'Home':
          nextIdx = 0;
          break;
        case 'End':
          nextIdx = this._tabs.length - 1;
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          this._activate(currentIdx, { focus: true });
          return;
        default:
          return;
      }
      if (nextIdx < 0) return;
      event.preventDefault();
      this._activate(nextIdx, { focus: true });
    }

    _activate(idx, { focus }) {
      this._tabs.forEach((tab, i) => {
        const isActive = i === idx;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
        const panel = this._panels[i];
        if (!panel) return;
        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      if (focus) {
        const target = this._tabs[idx];
        if (target) {
          target.focus();
          this._scrollTabIntoViewIfSlider(target);
        }

        /* On mobile, scroll the component into view so the tab + content
           top are both visible after switching tabs. */
        const isMobile =
          typeof window.matchMedia === 'function' &&
          !window.matchMedia('(min-width: 990px)').matches;
        if (isMobile) {
          const reduce =
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          requestAnimationFrame(() => {
            this.scrollIntoView({
              behavior: reduce ? 'auto' : 'smooth',
              block: 'start',
            });
          });
        }
      }

      this.dispatchEvent(
        new CustomEvent('hw-tabs:activate', {
          bubbles: true,
          detail: {
            index: idx,
            panel: this._panels[idx] || null,
          },
        })
      );
    }
  }

  customElements.define('hw-tabs', HwTabs);
})();
